import { Helmet } from 'react-helmet-async';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import isPast from 'date-fns/isPast';
import { useDispatch } from 'react-redux';

import ArchiveRoundedIcon from '@mui/icons-material/ArchiveRounded';
import {
	Box,
	Stack,
	Button,
	Popover,
	Tooltip,
	MenuItem,
	Divider,
	Dialog,
	NativeSelect,
	Typography,
	IconButton,
	Grid,
} from '@mui/material';
import { DataGrid, Toolbar, ToolbarButton } from '@mui/x-data-grid';

// components
import Iconify from '../../../components/iconify';
import {
	ToolBarSearch,
	ToolBarColumnToggler,
	Container as DataGridContainer,
	dataGridSx,
} from '../../../components/datagrid';
import { createRequestsDataSource } from '../../../components/datagrid/data-source/request';
import { Status, PaymentStatus, AssessmentDatePopUp } from '../../../components/request';
import { type RequestProps, type Status as RequestStatus, Request } from '../../../models';
import { fDateTime } from '../../../utils/formatTime';
import { useRole } from '../../../hooks/useRole';
import { usePermissions } from '../../../hooks/usePermissions';
import { calculateNextValidDate, assessmentTimeConfig } from '../../../utils/assessmentDateTimeCalculator';

import {
	useDelRequestMutation,
	useUpdateRequestAssessmentDateMutation,
	useUpdateRequestStatusMutation,
} from '../../../slices/apis/requestApi';
import type { GetRequestsResponseData } from '../../../slices/apis/apis.types';
import type { AppDispatch } from '../../../store';

// ----------------------------------------------------------------------

export default function Requests() {
	const useAppDispatch = useDispatch.withTypes<AppDispatch>();
	const dispatch = useAppDispatch();
	const { isStaff, isAdmin } = useRole();
	const { can } = usePermissions();
	const canEditAssessmentDate = can('req:update:asmDates');

	const [requestEditable, setRequestEditable] = useState(false);
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [archivedRequest, setArchivedRequest] = useState(false);

	const dataSource = useMemo(
		() => createRequestsDataSource(dispatch, { isArchived: archivedRequest }),
		[dispatch, archivedRequest],
	);

	const navigate = useNavigate();

	/*
		request deletion
	*/
	const [selectedRequest, setSelectedRequest] = useState<RequestProps | null>(null);
	const [delRequest, { isSuccess: isDelReqSuccess, isLoading: isDelReqLoading }] = useDelRequestMutation();
	const [requestDeleteConfirmDialogOpen, setRequestDeleteConfirmDialogOpen] = useState<boolean>(false);

	/*
		request assessment date editing
	*/
	const [assessmentDateDialogOpen, setAssessmentDateDialogOpen] = useState<boolean>(false);
	const [assessmentDate, setAssessmentDate] = useState<Date | null>(null);
	const [updateAssessmentDate, { isSuccess: isUpdateAssessmentDateSuccess, isLoading: isUpdateAssessmentDateLoading }] =
		useUpdateRequestAssessmentDateMutation();

	const handleOpenAssessmentDateDialog = useCallback((request: GetRequestsResponseData) => {
		const assessmentDate = request.assessmentDates[0];
		const date =
			!assessmentDate || isPast(new Date(assessmentDate))
				? calculateNextValidDate(new Date(), assessmentTimeConfig)
				: new Date(assessmentDate);

		setAssessmentDate(date);
		setSelectedRequest(request);
		setAssessmentDateDialogOpen(true);
	}, []);

	const handleNavigate = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			const route = e.currentTarget.getAttribute('data-route');
			if (route) {
				navigate(route);
			}
		},
		[navigate],
	);

	const handleCloseAssessmentDateDialog = () => {
		setAssessmentDateDialogOpen(false);
	};

	const onAssessmentDateChange = (newValue: Date) => {
		setAssessmentDate(newValue);
	};

	const onAssessmentDateUpdate = () => {
		if (selectedRequest?.id) {
			updateAssessmentDate({
				id: selectedRequest.id,
				assessmentDate,
			});
		}
	};

	/*
		request status editing
	*/
	const [statusEditingDialogOpen, setStatusEditingDialogOpen] = useState<boolean>(false);
	const [updateRequestStatus, { isSuccess: isUpdateReqStatusSuccess, isLoading: isUpdateReqStatusLoading }] =
		useUpdateRequestStatusMutation();

	let statusOptions = [] as RequestStatus[];

	statusOptions = isStaff ? Request.getAllowedEditableStatusStaff() : Request.getAllowedEditableStatusAdmin();

	const handleOpenStatusEditingDialog = useCallback(
		(request: GetRequestsResponseData) => {
			if (Request.isStatusEditingAllowed(request.status, isAdmin)) {
				setStatusEditingDialogOpen(true);
				setSelectedRequest(request);
			}
		},
		[isAdmin],
	);

	const onStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		if (!statusOptions.includes(e.target.value as RequestStatus)) {
			return;
		}

		setSelectedRequest({
			...selectedRequest,
			status: e.target.value,
		} as RequestProps);
	};

	const onStatusUpdate = () => {
		if (selectedRequest) {
			const { id, status } = selectedRequest;
			updateRequestStatus({
				id,
				status,
			});
		}
	};

	const handleCloseStatusEditingDialog = () => {
		setStatusEditingDialogOpen(false);
	};

	const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>, request: GetRequestsResponseData) => {
		setAnchorEl(event.currentTarget);
		setSelectedRequest(request);
		setRequestEditable(Request.isEditable(request.status));
	};

	const handleCloseMenu = () => {
		setAnchorEl(null);
	};

	const handleEditRequest = () => {
		if (selectedRequest?.id) {
			navigate(`/dashboard/requests/${selectedRequest.id}/edit`);
		}
	};

	const handleOpenRequestDetail = () => {
		if (selectedRequest?.id) {
			navigate(`/dashboard/requests/${selectedRequest.id}/view`);
		}
	};

	/*
		del request
	*/
	const handleDeleteRequest = () => {
		setAnchorEl(null);
		setRequestDeleteConfirmDialogOpen(true);
	};

	const handleCloseRequestDeleteConfirmDialog = () => {
		setRequestDeleteConfirmDialogOpen(false);
	};

	const onRequestDeleteConfirm = () => {
		if (selectedRequest?.id) {
			delRequest(selectedRequest.id);
		}
	};

	/*
		handle archived switch
	*/
	const handleArchivedSwitch = () => {
		setArchivedRequest((current) => !current);
	};

	useEffect(() => {
		if (isUpdateAssessmentDateSuccess) {
			setAssessmentDateDialogOpen(false);
			navigate('/dashboard/requests');
		}

		if (isDelReqSuccess) {
			setRequestDeleteConfirmDialogOpen(false);
		}

		if (isUpdateReqStatusSuccess) {
			setStatusEditingDialogOpen(false);
			navigate('/dashboard/requests');
		}
	}, [isDelReqSuccess, isUpdateReqStatusSuccess, isUpdateAssessmentDateSuccess, navigate]);

	return (
		<>
			<Helmet>
				<title> Requests - {process.env.REACT_APP_ORG} </title>
			</Helmet>
			<Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
				<Stack spacing={4} mb={3}>
					{can('req:create') && (
						<Box>
							<Button
								variant="contained"
								color="primary"
								data-route="/dashboard/requests/new"
								onClick={handleNavigate}
								startIcon={<Iconify icon="eva:plus-fill" />}
							>
								New Single Request
							</Button>
						</Box>
					)}
				</Stack>

				<>
					<DataGridContainer>
						<DataGrid
							slots={{
								toolbar: () => (
									<Toolbar>
										<Box sx={{ pl: 1 }}>
											<ToolBarSearch />
										</Box>
										<Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
											<Tooltip title="Columns">
												<ToolBarColumnToggler />
											</Tooltip>
											<Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 0.5 }} />
											<Tooltip title="Archived Requests">
												<ToolbarButton
													color={archivedRequest ? 'primary' : 'default'}
													aria-pressed={archivedRequest}
													onClick={handleArchivedSwitch}
												>
													<ArchiveRoundedIcon fontSize="small" />
												</ToolbarButton>
											</Tooltip>
										</Box>
									</Toolbar>
								),
							}}
							slotProps={{
								loadingOverlay: {
									variant: 'skeleton',
									noRowsVariant: 'skeleton',
								},
							}}
							disableColumnFilter
							disableDensitySelector
							disableColumnSelector
							showToolbar
							dataSource={dataSource}
							columnHeaderHeight={48}
							sx={dataGridSx}
							columns={[
								{
									field: 'id',
									headerName: 'ID',
									resizable: false,
									sortable: false,
									flex: 0.25,
								},
								{
									field: 'candidateName',
									headerName: 'Name',
									resizable: false,
									flex: 0.8,
								},
								{
									field: 'phone',
									headerName: 'Phone',
									resizable: false,
									sortable: false,
									flex: 0.6,
								},
								{
									field: 'user',
									headerName: 'User',
									resizable: false,
									sortable: false,
									flex: 0.7,
								},
								{
									field: 'employer',
									headerName: 'Employer',
									resizable: false,
									sortable: false,
									flex: 0.7,
								},
								{
									field: 'testCentre',
									headerName: 'Test Centre',
									resizable: false,
									sortable: false,
									flex: 0.7,
								},
								{
									field: 'assessmentDate',
									headerName: 'Asssessment Date',
									resizable: false,
									flex: 0.8,
									renderCell: (params) => {
										const {
											row: { id, status },
										} = params;

										const disableAssessmentSchedulling = Request.disableAssessmentSchedulling(status);
										const assessmentDate = params.value ? fDateTime(params.value) : 'Pending';
										const isAssessmentDateEditable = canEditAssessmentDate && !disableAssessmentSchedulling;

										return (
											<Typography
												color="primary"
												variant="subtitle2"
												data-request-id={id}
												onClick={
													isAssessmentDateEditable
														? () => handleOpenAssessmentDateDialog(params.row.request)
														: undefined
												}
												sx={{
													display: 'flex',
													alignItems: 'center',
													height: '100%',
													width: '100%',
													cursor: disableAssessmentSchedulling ? 'default' : 'pointer',
													'&:focus': {
														cursor: disableAssessmentSchedulling ? 'default' : 'pointer',
													},
													'&:focus-visible': {
														cursor: disableAssessmentSchedulling ? 'default' : 'pointer',
													},
												}}
											>
												{assessmentDate}
											</Typography>
										);
									},
								},
								{
									field: 'paymentStatus',
									headerName: 'Payment Status',
									resizable: false,
									flex: 0.8,
									renderCell: (params) => {
										const { row } = params;
										return <PaymentStatus paymentStatus={row.paymentStatus || 'Unknown'} />;
									},
								},
								{
									field: 'status',
									headerName: 'Request Status',
									resizable: false,
									flex: 0.92,
									renderCell: (params) => {
										const { row } = params;

										return (
											<Status
												status={row.status}
												clickable
												correlationId={row.id}
												onClick={() => handleOpenStatusEditingDialog(params.row.request)}
											/>
										);
									},
								},
								{
									field: 'actions',
									headerName: 'Edit',
									headerAlign: 'center',
									align: 'right',
									flex: 0.3,
									sortable: false,
									filterable: false,
									resizable: false,
									renderCell: (params) => {
										const {
											row: { id },
										} = params;

										return (
											<IconButton
												size="large"
												color="inherit"
												data-request-id={id}
												onClick={(event) => handleOpenMenu(event, params.row.request)}
											>
												<Iconify icon={'eva:more-vertical-fill'} />
											</IconButton>
										);
									},
								},
							]}
						/>
					</DataGridContainer>

					{/* request menu options popover */}
					<Popover
						open={Boolean(anchorEl)}
						onClose={handleCloseMenu}
						anchorEl={anchorEl}
						anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
						transformOrigin={{ vertical: 'top', horizontal: 'right' }}
						PaperProps={{
							sx: {
								p: 1,
								width: 140,
								'& .MuiMenuItem-root': {
									px: 1,
									typography: 'body2',
									borderRadius: 0.75,
								},
							},
						}}
					>
						<MenuItem onClick={handleOpenRequestDetail}>
							<Iconify icon={'eva:file-text-outline'} sx={{ mr: 1 }} />
							View
						</MenuItem>

						{!requestEditable ? null : (
							<MenuItem onClick={handleEditRequest}>
								<Iconify icon={'eva:edit-fill'} sx={{ mr: 1 }} />
								Edit
							</MenuItem>
						)}

						<MenuItem sx={{ color: 'error.main' }} onClick={handleDeleteRequest}>
							<Iconify icon={'eva:trash-2-outline'} sx={{ mr: 1 }} />
							Delete
						</MenuItem>
					</Popover>

					{/* assessment date editing */}
					<Dialog
						open={Boolean(assessmentDateDialogOpen)}
						onClose={handleCloseAssessmentDateDialog}
						title={`Choose an assessment date/time for request ${selectedRequest?.id}`}
					>
						<Grid container spacing={2} sx={{ justifyContent: 'center', alignItems: 'center', m: 2 }}>
							<Grid>
								<AssessmentDatePopUp
									initialDate={assessmentDate ?? new Date()}
									onChange={onAssessmentDateChange}
									reminder="By confirming the assessment date/time, a confirmation email will be sent to the candidate. Please double check the date/time selected."
								/>
							</Grid>
							<Grid>
								<Button
									loading={isUpdateAssessmentDateLoading}
									variant="outlined"
									color="primary"
									onClick={onAssessmentDateUpdate}
								>
									<span>Confirm</span>
								</Button>
							</Grid>
							<Grid>
								<Button color="error" variant="outlined" onClick={handleCloseAssessmentDateDialog}>
									Cancel
								</Button>
							</Grid>
						</Grid>
					</Dialog>

					{/* status editing */}
					<Dialog
						open={Boolean(statusEditingDialogOpen)}
						onClose={handleCloseStatusEditingDialog}
						title={`Change request ${selectedRequest?.id} status`}
					>
						<Grid container spacing={2} sx={{ m: 2 }}>
							<Grid size={{ md: 12 }}>
								<NativeSelect
									defaultValue={selectedRequest?.status}
									inputProps={{
										name: 'status',
										id: 'uncontrolled-native',
										onChange: onStatusChange,
									}}
								>
									{['--- Please Select ---', ...statusOptions].map((status, index) => (
										<option key={status} value={status}>
											{status}
										</option>
									))}
								</NativeSelect>
							</Grid>
							<Grid size={{ md: 6 }}>
								<Button loading={isUpdateReqStatusLoading} variant="outlined" color="primary" onClick={onStatusUpdate}>
									<span>Confirm</span>
								</Button>
							</Grid>
							<Grid size={{ md: 6 }}>
								<Button color="error" variant="outlined" onClick={handleCloseStatusEditingDialog}>
									Cancel
								</Button>
							</Grid>
						</Grid>
					</Dialog>

					{/* request delete confirm */}
					<Dialog
						open={Boolean(requestDeleteConfirmDialogOpen)}
						onClose={handleCloseRequestDeleteConfirmDialog}
						title={'Warning'}
					>
						<Grid container spacing={2} sx={{ m: 2 }}>
							<Grid size={{ md: 12 }}>
								<Typography variant="subtitle2">{`Confirm to delete request ${selectedRequest?.id}`}</Typography>
							</Grid>
							<Grid size={{ md: 6 }}>
								<Button loading={isDelReqLoading} variant="outlined" color="primary" onClick={onRequestDeleteConfirm}>
									<span>Confirm</span>
								</Button>
							</Grid>
							<Grid size={{ md: 6 }}>
								<Button color="error" variant="outlined" onClick={handleCloseRequestDeleteConfirmDialog}>
									Cancel
								</Button>
							</Grid>
						</Grid>
					</Dialog>
				</>
			</Box>
		</>
	);
}
